import { useState, type FormEvent } from 'react'
import { Button } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { useAuth } from '@/lib/auth'
import { appCopy } from '../copy'

/**
 * Device offline-encryption gate (OFFLINE_&_MOBILE_RESILIENCE.md §9),
 * mirroring EnrollTwoFactorPage's full-screen pattern. Rendered by
 * RequireAuth (apps/web/src/app/App.tsx) whenever the app can't safely read
 * this user's encrypted on-device data yet. Two mutually exclusive modes,
 * decided by the caller only rendering this gate when exactly one applies:
 *
 *  - unlock: this user already has a PIN-wrapped data key on this device,
 *    but the codec isn't wired into the offline runtime for this page
 *    session yet — the key material is never persisted, so every fresh
 *    load starts locked and needs the PIN again.
 *  - force-enroll: the company policy `requireOfflineEncryption` is on and
 *    this user has not set a PIN on this device yet.
 *
 * CRITICAL: a PIN only unlocks the LOCAL encryption key. It is never a
 * server credential and never mints or touches a session — this gate calls
 * only `unlockOffline`/`enableOfflineEncryption`, never anything that could
 * fabricate an access token.
 */
export function OfflineUnlockGate() {
  const copy = useCopy(appCopy)
  const { user, offlineEncryption, unlockOffline, enableOfflineEncryption, logout } = useAuth()
  const mode: 'unlock' | 'force-enroll' = offlineEncryption.enrolled ? 'unlock' : 'force-enroll'
  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | undefined>(undefined)

  async function submitUnlock(event: FormEvent) {
    event.preventDefault()
    setMessage(undefined)
    setBusy(true)
    try {
      await unlockOffline(pin)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : copy.authCommon.pinNotAccepted)
    } finally {
      setBusy(false)
    }
  }

  async function submitEnroll(event: FormEvent) {
    event.preventDefault()
    setMessage(undefined)
    if (pin !== confirmPin) {
      setMessage(copy.authCommon.pinsDoNotMatch)
      return
    }
    setBusy(true)
    try {
      await enableOfflineEncryption(pin)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : copy.offlineUnlock.setupError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6">
        <div>
          <h1 className="text-lg font-semibold">
            {mode === 'unlock' ? copy.offlineUnlock.unlockTitle : copy.offlineUnlock.setupTitle}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === 'unlock'
              ? copy.offlineUnlock.unlockDescription({ account: user?.email ?? copy.offlineUnlock.accountFallback })
              : copy.offlineUnlock.setupDescription({ account: user?.email ?? copy.offlineUnlock.accountFallback })}
          </p>
        </div>

        {mode === 'unlock' ? (
          <form onSubmit={(e) => void submitUnlock(e)} className="space-y-3 text-sm">
            <input aria-label={copy.offlineUnlock.pin}
              required
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={copy.offlineUnlock.pin}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
            />
            {message && <p className="text-sm text-destructive">{message}</p>}
            <Button className="w-full"
 type="submit"
 disabled={busy}

 >
              {busy ? copy.offlineUnlock.unlocking : copy.offlineUnlock.unlock}
            </Button>
          </form>
        ) : (
          <form onSubmit={(e) => void submitEnroll(e)} className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              {copy.offlineUnlock.choosePin}
            </p>
            <input aria-label={copy.offlineUnlock.newPin}
              required
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={copy.offlineUnlock.newPin}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
            />
            <input aria-label={copy.offlineUnlock.confirmPin}
              required
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder={copy.offlineUnlock.confirmPin}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2"
            />
            {message && <p className="text-sm text-destructive">{message}</p>}
            <Button className="w-full"
 type="submit"
 disabled={busy}

 >
              {busy ? copy.offlineUnlock.settingUp : copy.offlineUnlock.setPin}
            </Button>
          </form>
        )}

        <Button
          variant="link"
          size="inline"
          onClick={() => void logout()}
          className="w-full text-center text-muted-foreground"
        >
          {copy.authCommon.signOut}
        </Button>
        {/* FF-AUDIT-098: what Sign out costs, beside the PIN field. */}
        {mode === 'unlock' && <p className="text-center text-xs text-muted-foreground">{copy.offlineUnlock.signOutHint}</p>}
      </div>
    </div>
  )
}
